// components/FirmwareUpload.tsx
import React, { useEffect, useRef, useState } from 'react';
import { ESPLoader, Transport } from 'esptool-js';
import './FirmwareUpload.css';

declare global {
  interface Navigator {
    serial?: any;
  }
}

interface BinFile {
  name: string;
  download_url: string;
  size: number;
}

interface FlashFile {
  data: Uint8Array;
  address: number;
}

const GITHUB_API = 'https://api.github.com/repos/kdi6033/download/contents/';

// ─────────────────────────────────────────────────────────────────────────
// 파일 타입 판별
// .merged.bin  → 통합 바이너리 (부트로더+파티션+앱 전체), 주소 0x0
// .ino.bin     → 앱 바이너리만, 주소 0x10000  ← 별도 부트로더·파티션 불필요
//               (보드에 이미 부트로더가 설치된 경우 또는 OTA 업데이트 용도)
// 기타 .bin    → 사용자가 주소를 직접 지정
// ─────────────────────────────────────────────────────────────────────────
function getFileType(name: string): 'merged' | 'ino' | 'raw' {
  if (name.endsWith('.merged.bin')) return 'merged';
  if (name.endsWith('.ino.bin'))    return 'ino';
  return 'raw';
}

const FirmwareUpload: React.FC = () => {
  const [supported, setSupported]       = useState(false);
  const [binFiles, setBinFiles]         = useState<BinFile[]>([]);
  const [filesError, setFilesError]     = useState('');
  const [selectedFile, setSelectedFile] = useState<BinFile | null>(null);
  const [port, setPort]                 = useState<any>(null);
  const [isConnected, setIsConnected]   = useState(false);
  const [isFlashing, setIsFlashing]     = useState(false);
  const [progress, setProgress]         = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [logs, setLogs]                 = useState<string[]>([]);
  const [status, setStatus]             = useState('대기 중');
  const [flashAddress, setFlashAddress] = useState('0x0');
  const logEndRef = useRef<HTMLDivElement>(null);

  const fileType = selectedFile ? getFileType(selectedFile.name) : null;
  const isInoBin = fileType === 'ino';     // 앱 바이너리만
  const isMerged = fileType === 'merged';  // 통합 바이너리

  useEffect(() => { setSupported(!!navigator.serial); }, []);

  useEffect(() => {
    fetch(GITHUB_API)
      .then(r => { if (!r.ok) throw new Error(`GitHub API ${r.status}`); return r.json(); })
      .then((data: any[]) => {
        const bins = data.filter(f => f.name.endsWith('.bin'));
        setBinFiles(bins);
        if (!bins.length) setFilesError('저장소에 .bin 파일이 없습니다.');
      })
      .catch(() => setFilesError('GitHub 파일 목록을 가져올 수 없습니다.'));
  }, []);

  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [logs]);

  const addLog = (msg: string) => {
    const t = new Date().toLocaleTimeString('ko-KR');
    setLogs(prev => [...prev, `[${t}] ${msg}`]);
  };

  const connectPort = async () => {
    try {
      const p = await navigator.serial.requestPort();
      setPort(p);
      setIsConnected(true);
      setStatus('포트 연결됨');
      addLog('시리얼 포트가 선택되었습니다.');
    } catch (e: any) {
      if (e?.name !== 'NotFoundError') addLog('포트 오류: ' + e?.message);
    }
  };

  const disconnectPort = async () => {
    if (port) { try { await port.close(); } catch (_) {} setPort(null); }
    setIsConnected(false);
    setStatus('대기 중');
    addLog('포트 연결이 해제되었습니다.');
  };

  const flashFirmware = async () => {
    if (!port || !selectedFile) return;
    setIsFlashing(true);
    setProgress(0);
    setProgressLabel('');
    setStatus('플래싱 중...');

    const terminal = {
      clean: () => {},
      writeLine: (d: string) => addLog(d),
      write: (d: string) => addLog(d),
    };

    let transport: Transport | null = null;

    try {
      const flashFiles: FlashFile[] = [];

      if (isMerged) {
        // ── 통합 바이너리: 0x0 한 번에 플래싱 ──────────────────────────
        addLog(`▶ GitHub에서 다운로드 중: ${selectedFile.name}`);
        addLog('  → 통합 바이너리(.merged.bin) — 0x0 주소에 전체 플래싱합니다.');
        const res = await fetch(selectedFile.download_url);
        if (!res.ok) throw new Error('파일 다운로드 실패');
        const data = new Uint8Array(await res.arrayBuffer());
        flashFiles.push({ data, address: 0x0 });
        addLog(`  → 다운로드 완료 (${(data.length / 1024).toFixed(1)} KB)`);

      } else if (isInoBin) {
        // ── 앱 바이너리만: 0x10000 에 플래싱 (부트로더는 건드리지 않음) ──
        addLog(`▶ GitHub에서 다운로드 중: ${selectedFile.name}`);
        addLog('  → 앱 바이너리(.ino.bin) — 0x10000 주소에만 플래싱합니다.');
        addLog('  ℹ 부트로더·파티션은 이미 보드에 설치된 것을 그대로 사용합니다.');
        const res = await fetch(selectedFile.download_url);
        if (!res.ok) throw new Error('앱 바이너리 다운로드 실패');
        const data = new Uint8Array(await res.arrayBuffer());
        flashFiles.push({ data, address: 0x10000 });
        addLog(`앱 바이너리 완료 (${(data.length / 1024).toFixed(1)} KB → 0x10000)`);

      } else {
        // ── 사용자 지정 주소 ────────────────────────────────────────────
        const addr = parseInt(flashAddress, 16);
        if (isNaN(addr)) throw new Error('유효하지 않은 플래시 주소');
        addLog(`▶ GitHub에서 다운로드 중: ${selectedFile.name}`);
        addLog(`  → 사용자 지정 주소(0x${addr.toString(16)})에 플래싱합니다.`);
        const res = await fetch(selectedFile.download_url);
        if (!res.ok) throw new Error('파일 다운로드 실패');
        const data = new Uint8Array(await res.arrayBuffer());
        flashFiles.push({ data, address: addr });
        addLog(`완료 (${(data.length / 1024).toFixed(1)} KB → 0x${addr.toString(16)})`);
      }

      const totalBytes = flashFiles.reduce((s, f) => s + f.data.length, 0);

      // ESPLoader 연결
      transport = new Transport(port, false);
      const espLoader = new ESPLoader({ transport, baudrate: 115200, terminal });

      addLog('ESP32 부트로더 연결 중... (자동 리셋 안 될 경우 BOOT 버튼을 누르세요)');
      const chip = await espLoader.main();
      addLog(`칩 감지: ${chip}`);
      addLog(`플래싱 시작 (${flashFiles.length}개 영역, 총 ${(totalBytes / 1024).toFixed(1)} KB)...`);

      await espLoader.writeFlash({
        fileArray: flashFiles,
        flashMode: 'keep',
        flashFreq: 'keep',
        flashSize: 'keep',
        eraseAll: false,
        compress: true,
        reportProgress: (_fileIndex: number, written: number, total: number) => {
          setProgress(Math.round((written / total) * 100));
          setProgressLabel(`${Math.round((written / total) * 100)}% (${(written / 1024).toFixed(0)} / ${(total / 1024).toFixed(0)} KB)`);
        },
      });

      addLog('✅ 업로드 완료! 보드를 재시작합니다...');
      await espLoader.after('hard_reset');
      setProgress(100);
      setProgressLabel('완료');
      setStatus('완료');
    } catch (e: any) {
      addLog('❌ 오류: ' + (e?.message || String(e)));
      setStatus('오류 발생');
    } finally {
      if (transport) { try { await transport.disconnect(); } catch (_) {} }
      setIsFlashing(false);
    }
  };

  return (
    <div className="fw-wrap">
      <h2 className="fw-title">펌웨어 다운로드</h2>
      <p className="fw-info">
        크롬(Chrome) 또는 엣지(Edge) 브라우저에서만 동작합니다.<br />
        ESP32 보드를 USB로 연결한 후 포트를 선택하고 펌웨어를 선택하세요.
      </p>

      {/* 모델 참조 */}
      <div className="fw-model-ref">
        <p className="fw-model-ref-title">그림을 보고 모델을 확인하세요</p>
        <div className="fw-model-grid">
          {[
            { name: 'i2r-02',     desc: 'IoT PLC (4ch)',     img: 'https://github.com/kdi6033/i2r-02/raw/main/images/i2r-02-kc.png' },
            { name: 'i2r-02-hmi', desc: 'IoT PLC (4ch) HMI', img: 'https://github.com/kdi6033/i2r-02/raw/main/images/i2r-02-hmi-2.png' },
            { name: 'i2r-03',     desc: 'IoT PLC (4ch 센서)', img: 'https://github.com/kdi6033/i2r-03/raw/main/images/i2r-03-kc.png' },
          ].map(m => (
            <div key={m.name} className="fw-model-card">
              <img src={m.img} alt={m.name} className="fw-model-img" />
              <div className="fw-model-name">{m.name}</div>
              <div className="fw-model-desc">{m.desc}</div>
            </div>
          ))}
        </div>
        <p className="fw-model-more">
          <a href="https://github.com/kdi6033/i2r/blob/main/README.md#i2r-iot-plc--sensor"
             target="_blank" rel="noreferrer" className="fw-model-more-link">
            🔍 기타 기기 모델 보기 →
          </a>
        </p>
      </div>

      {!supported && (
        <p className="fw-warn">
          이 브라우저는 Web Serial API를 지원하지 않습니다. Chrome 또는 Edge를 사용하세요.
        </p>
      )}

      <div className="fw-status"><b>상태:</b> {status}</div>

      {/* 1. 포트 연결 */}
      <div className="fw-section">
        <h3 className="fw-section-title">1. 시리얼 포트 연결</h3>
        <div className="fw-row">
          <button className="fw-btn fw-btn-primary" onClick={connectPort}
            disabled={!supported || isConnected || isFlashing}>
            COM 포트 선택
          </button>
          <button className="fw-btn" onClick={disconnectPort}
            disabled={!isConnected || isFlashing}>
            연결 해제
          </button>
        </div>
        {isConnected && <p className="fw-connected">포트 연결됨</p>}
      </div>

      {/* 2. 펌웨어 선택 */}
      <div className="fw-section">
        <h3 className="fw-section-title">2. 펌웨어 선택</h3>
        {filesError ? (
          <p className="fw-warn">{filesError}</p>
        ) : binFiles.length === 0 ? (
          <p className="fw-loading">파일 목록 불러오는 중...</p>
        ) : (
          <div className="fw-file-list">
            {binFiles.filter(f => f.name.endsWith('.merged.bin')).map(f => (
              <div key={f.name} className={`fw-file-item ${selectedFile?.name === f.name ? 'fw-file-selected' : ''}`}>
                <label className="fw-file-label">
                  <input type="radio" name="binfile" value={f.name}
                    checked={selectedFile?.name === f.name}
                    onChange={() => {
                      setSelectedFile(f);
                      const ft = getFileType(f.name);
                      setFlashAddress(ft === 'ino' ? '0x10000' : '0x0');
                    }}
                  />
                  <span className="fw-file-name">{f.name.replace('.ino.merged.bin', '')}</span>
                  <span className="fw-file-size">{(f.size / 1024).toFixed(1)} KB</span>
                </label>
              </div>
            ))}
          </div>
        )}

        {isMerged && (
          <div className="fw-ino-info fw-info-merged">
            <b>✅ 통합 바이너리(.merged.bin)</b> — 0x0 주소에 전체를 한 번에 플래싱합니다.
            <p style={{ marginTop: '6px', fontSize: '0.85em', color: '#555' }}>실제 파일: <code>{selectedFile?.name}</code></p>
            <ul>
              <li>부트로더 + 파티션 테이블 + 앱이 모두 포함된 파일입니다.</li>
              <li>처음 설치 또는 초기화 시 이 파일을 사용하세요.</li>
            </ul>
          </div>
        )}

        {isInoBin && (
          <div className="fw-ino-info fw-info-ino">
            <b>⚠ 앱 바이너리만(.ino.bin)</b> — 0x10000 주소에만 플래싱합니다.
            <ul>
              <li>보드에 이미 부트로더가 설치되어 있어야 합니다.</li>
              <li>펌웨어 업데이트(OTA 아닌 경우) 시 사용하세요.</li>
              <li>처음 설치라면 <b>.merged.bin</b> 파일을 사용하는 것을 권장합니다.</li>
            </ul>
            <p className="fw-warn-note">❗ 부트 루프(반복 리셋)가 발생하면 .merged.bin으로 다시 플래싱하세요.</p>
          </div>
        )}
      </div>

      {/* 3. 플래시 주소 (raw bin 전용) */}
      {fileType === 'raw' && selectedFile && (
        <div className="fw-section">
          <h3 className="fw-section-title">3. 플래시 주소</h3>
          <input className="fw-input" type="text" value={flashAddress}
            onChange={e => setFlashAddress(e.target.value)}
            placeholder="0x0" disabled={isFlashing} />
          <p className="fw-hint">통합(merged) 바이너리: 0x0 | 앱 바이너리: 0x10000</p>
        </div>
      )}

      {/* 업로드 */}
      <div className="fw-section">
        <h3 className="fw-section-title">{fileType === 'raw' ? '4' : '3'}. 펌웨어 업로드</h3>
        <button className="fw-btn fw-btn-flash" onClick={flashFirmware}
          disabled={!isConnected || !selectedFile || isFlashing}>
          {isFlashing ? `플래싱 중... ${progress}%` : '펌웨어 업로드 시작'}
        </button>
        {isFlashing && (
          <>
            <div className="fw-progress-bar">
              <div className="fw-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            {progressLabel && <p className="fw-progress-label">{progressLabel}</p>}
          </>
        )}
      </div>

      {/* 로그 */}
      {logs.length > 0 && (
        <div className="fw-section">
          <h3 className="fw-section-title">로그</h3>
          <div className="fw-log">
            {logs.map((l, i) => <div key={i} className="fw-log-line">{l}</div>)}
            <div ref={logEndRef} />
          </div>
        </div>
      )}

    </div>
  );
};

export default FirmwareUpload;
